/**
 * Database Attachment Scanner for Export Size Dialog
 *
 * sl-ht07.5: Scans database blocks in docs for attachment/image blobs
 * and groups them by database with size information.
 */

import type { Store } from '@blocksuite/store';
import { DATABASE_ROW_SHARD_ROWS_MAP_KEY } from '@streamlain/database-identity';
import { Map as YMap } from 'yjs';

export interface DatabaseAttachmentInfo {
  databaseId: string;
  databaseTitle: string;
  blobIds: Set<string>;
  fileCount: number;
  totalSize: number; // in bytes, populated externally after scanning
}

type DatabaseBlobColumns = {
  attachmentColumnIds: Set<string>;
  imageColumnIds: Set<string>;
};

type DatabaseAttachmentAccumulator = DatabaseAttachmentInfo &
  DatabaseBlobColumns;

const SERVER_DATABASE_ROW_SHARD_DOC_ID =
  /^db\$[^$]+\$database\$([^$]+)\$rows\$[^$]+$/;

/**
 * Scans all database blocks in the given docs for attachment/image blobs.
 * Returns per-database info with blob IDs (sizes must be resolved separately).
 */
export function scanDatabaseAttachments(
  stores: Store[]
): DatabaseAttachmentInfo[] {
  const databases = new Map<string, DatabaseAttachmentAccumulator>();

  for (const store of stores) {
    const root = store.root;
    if (!root) continue;

    walkBlocks(root, block => {
      if (block.flavour !== 'affine:database') return;

      const columns = block.props.columns as
        | Array<{ id?: string; type?: string }>
        | undefined;
      if (!columns) return;

      const databaseIdValue = block.props.databaseId;
      const databaseId =
        typeof databaseIdValue === 'string' && databaseIdValue
          ? databaseIdValue
          : block.id;
      const info =
        databases.get(databaseId) ??
        createDatabaseAttachmentAccumulator(databaseId, block.props.title);
      databases.set(databaseId, info);

      for (const col of columns) {
        if (col.type === 'attachment' && col.id) {
          info.attachmentColumnIds.add(col.id);
        } else if (col.type === 'image' && col.id) {
          info.imageColumnIds.add(col.id);
        }
      }

      // Database cells are stored on the DATABASE block's props.cells,
      // keyed by rowId (child block ID) → columnId → CellDataType
      const dbCells = block.props.cells as
        | Record<string, Record<string, { columnId?: string; value?: unknown }>>
        | undefined;
      if (!dbCells) return;

      for (const child of block.children) {
        const rowCells = dbCells[child.id];
        if (!rowCells) continue;
        collectRowBlobIds(info, rowCells);
      }
    });
  }

  // Row-sharded databases keep their cell values in dedicated Y.Doc stores.
  // The source/root database block above remains authoritative for columns and
  // title; the shard only contributes row values.
  for (const store of stores) {
    const databaseId = parseDatabaseRowShardDatabaseId(store.id);
    if (!databaseId) continue;

    const info = databases.get(databaseId);
    if (!info) continue;

    store.spaceDoc
      .getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY)
      .forEach(row => collectRowBlobIds(info, row));
  }

  return [...databases.values()]
    .filter(info => info.blobIds.size > 0)
    .map(({ attachmentColumnIds: _, imageColumnIds: __, ...info }) => ({
      ...info,
      fileCount: info.blobIds.size,
    }));
}

function createDatabaseAttachmentAccumulator(
  databaseId: string,
  rawTitle: unknown
): DatabaseAttachmentAccumulator {
  const title =
    typeof rawTitle === 'string'
      ? rawTitle
      : rawTitle &&
          typeof (rawTitle as { toString?: () => string }).toString ===
            'function'
        ? (rawTitle as { toString: () => string }).toString()
        : '';

  return {
    attachmentColumnIds: new Set(),
    blobIds: new Set(),
    databaseId,
    databaseTitle: title || 'Untitled Database',
    fileCount: 0,
    imageColumnIds: new Set(),
    totalSize: 0,
  };
}

function parseDatabaseRowShardDatabaseId(docId: string): string | null {
  return SERVER_DATABASE_ROW_SHARD_DOC_ID.exec(docId)?.[1] ?? null;
}

function readProperty(value: unknown, key: string): unknown {
  if (value instanceof YMap) return value.get(key);
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return (value as Record<string, unknown>)[key];
  }
  return undefined;
}

function readCell(row: unknown, columnId: string): unknown {
  return readProperty(row, columnId);
}

function collectAttachmentBlobIds(target: Set<string>, value: unknown): void {
  if (value instanceof YMap) {
    value.forEach((_, blobId) => {
      if (blobId) target.add(blobId);
    });
    return;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return;
  Object.keys(value as Record<string, unknown>).forEach(blobId => {
    if (blobId) target.add(blobId);
  });
}

function collectRowBlobIds(
  info: DatabaseAttachmentAccumulator,
  row: unknown
): void {
  for (const columnId of info.attachmentColumnIds) {
    collectAttachmentBlobIds(
      info.blobIds,
      readProperty(readCell(row, columnId), 'value')
    );
  }
  for (const columnId of info.imageColumnIds) {
    const value = readProperty(readCell(row, columnId), 'value');
    if (typeof value === 'string' && value) info.blobIds.add(value);
  }
}

/**
 * Resolves blob sizes for scanned database info.
 * Uses workspace blob storage to look up each blob's size.
 */
export async function resolveBlobSizes(
  infos: DatabaseAttachmentInfo[],
  getBlobSize: (blobId: string) => Promise<number>
): Promise<void> {
  for (const info of infos) {
    let total = 0;
    for (const blobId of info.blobIds) {
      total += await getBlobSize(blobId);
    }
    info.totalSize = total;
  }
}

/**
 * Collects all blob IDs from excluded (unchecked) databases.
 */
export function collectExcludedBlobIds(
  infos: DatabaseAttachmentInfo[],
  excludedDatabaseIds: Set<string>
): Set<string> {
  const excluded = new Set<string>();
  for (const info of infos) {
    if (excludedDatabaseIds.has(info.databaseId)) {
      for (const blobId of info.blobIds) {
        excluded.add(blobId);
      }
    }
  }
  return excluded;
}

// ─── Internal ────────────────────────────────────────────────────────────────

interface BlockLike {
  id: string;
  flavour: string;
  props: Record<string, unknown>;
  children: BlockLike[];
}

function walkBlocks(block: BlockLike, fn: (b: BlockLike) => void): void {
  fn(block);
  for (const child of block.children) {
    walkBlocks(child, fn);
  }
}
